import React, { useState, useMemo, useEffect } from "react";
import { connect } from "react-redux";

import { bindActionCreators, Dispatch } from "redux";

import { FilterSection, FilterItem, FilterItemField, PickerInput, RequiredState, DataColumnDef, InlineOverlay, Dropdown, FilterTextInput, Checkbox } from "@plex/react-components";

import { arrayHelpers } from "infrastructure/helpers";
import { ILocalizable, ILocale, commonMessages } from "infrastructure/localization";
import { ISettingsState } from "ui/app";
import { useLocalizedValues, useLocalizedDefaultLocale, useLocalizer } from "ui/hooks";
import { displayMessages } from "ui/screens/translation-managers/translation-managers.localization";
import { IPickerSearchCriterion } from "ui/shared/helpers/plex-components";
import { IIndustry } from "ui/shared/models/common";

import { plexTranslationMessagesActions } from "../PlexTranslationMessages.actions";
import { IPlexTranslationMessagesManagerState, IPlexTranslationMessagesManagerFilterData, IAccount } from "../PlexTranslationMessages.state";

interface IFilterProps {
  locales: ILocale[];
  supportedLocales?: ILocale[];
  currentLocaleIdentifier: string;
  assignedToAccounts: IAccount[];
  industries: IIndustry[];
  statuses: ILocalizable[];
  parents: ILocalizable[];
  applyFilter: (data: IPlexTranslationMessagesManagerFilterData) => void;
  search: () => void;
  loadAssignedToAccounts: (criterion: IPickerSearchCriterion<ILocale>) => void;
  loadIndustries: (criterion: IPickerSearchCriterion<IIndustry>) => void;
  loadLocales: (criterion: IPickerSearchCriterion<ILocale>) => void;
  loadSupportedLocales: () => void;
}

export const PlexTranslationMessagesManagerFilter: React.FC<IFilterProps> = ({
  locales,
  supportedLocales,
  currentLocaleIdentifier,
  assignedToAccounts,
  industries,
  statuses,
  parents,
  applyFilter,
  search,
  loadAssignedToAccounts,
  loadIndustries,
  loadLocales,
  loadSupportedLocales
}) => {
  const localize = useLocalizer();
  const defaultLocale = useLocalizedDefaultLocale();

  const localizedValues = useLocalizedValues(() => {
    return {
      locale: displayMessages.filter("locale"),
      localeIdentifier: displayMessages.property("localeIdentifier"),
      localeName: displayMessages.property("localeName"),
      industry: displayMessages.filter("industry"),
      text: displayMessages.filter("text"),
      messageId: displayMessages.filter("messageId"),
      status: displayMessages.filter("status"),
      assignedTo: displayMessages.filter("assignedTo"),
      parent: displayMessages.filter("parent"),
      showTextPreview: displayMessages.filter("showTextPreview"),
      code: displayMessages.property("code"),
      description: displayMessages.property("description"),
      name: displayMessages.property("name"),
      all: commonMessages.property("all")
    };
  });

  const [state, setState] = useState<IPlexTranslationMessagesManagerFilterData & { localePickerInitialized: boolean }>({
    localePickerInitialized: false,
    isCheckedShowPreviewText: false,
    filterMessageId: "",
    filterText: ""
  });
  const [pinned, setPinned] = useState(false);

  const onFilterChange = <T, P extends keyof IPlexTranslationMessagesManagerFilterData>(value: T, prop: P) => {
    setState({ ...state, [prop]: value });
  };

  const onFilterSelect = <T, P extends keyof IPlexTranslationMessagesManagerFilterData>(items: T[], prop: P) => {
    setState({ ...state, [prop]: items && items.length ? items[0] : undefined });
  };

  const onCheckboxChange = e => {
    setState({
      ...state,
      [e.currentTarget.id]: e.currentTarget.checked
    });
  };

  const selectedLocales = useMemo(() => {
    return arrayHelpers.createFrom(state.selectedLocale);
  }, [state.selectedLocale]);

  const selectedIndustries = useMemo(() => {
    return arrayHelpers.createFrom(state.selectedIndustry);
  }, [state.selectedIndustry]);

  const selectedAssignedTo = useMemo(() => {
    return arrayHelpers.createFrom(state.selectedAssignedTo);
  }, [state.selectedAssignedTo]);

  useEffect(() => {
    loadSupportedLocales();
  }, [loadSupportedLocales]);

  useEffect(() => {
    if (supportedLocales && !state.localePickerInitialized) {
      setState(prevState => ({
        ...prevState,
        selectedLocale: supportedLocales.filter(e => e.localeIdentifier === currentLocaleIdentifier)[0] || defaultLocale,
        localePickerInitialized: true
      }));
    }
  }, [supportedLocales, currentLocaleIdentifier, state.localePickerInitialized, defaultLocale]);

  return (
    <FilterSection
      pinned={pinned}
      onSearch={() => {
        applyFilter(state);
        search();
      }}
      onPin={setPinned}
    >
      <FilterItem id="languageItem" labelText={localizedValues.locale} required={RequiredState.required}>
        <FilterItemField shouldCollapse={() => !state.selectedLocale}>
          <InlineOverlay show={!state.localePickerInitialized} spinnerPosition={"left"} showSpinner>
            <PickerInput
              autoPick
              dialogTitle={localizedValues.locale}
              data={locales}
              keySelector={row => row.localeIdentifier}
              displaySelector={row => row.name}
              selected={selectedLocales}
              onSelectionChanged={rows => onFilterSelect(rows, "selectedLocale")}
              onSearch={(q, cols) => loadLocales({ query: q, searchedColumns: cols })}
              searchText=""
            >
              <DataColumnDef<ILocale>
                key="localeIdentifier"
                id="localeIdentifier"
                title={localizedValues.localeIdentifier}
                valueSelector={row => row.localeIdentifier}
                searchable
                sortable
              >
                {row => row.localeIdentifier}
              </DataColumnDef>
              <DataColumnDef<ILocale> key="localeName" id="localeName" title={localizedValues.localeName} valueSelector={row => row.name} searchable sortable>
                {row => row.name}
              </DataColumnDef>
            </PickerInput>
          </InlineOverlay>
        </FilterItemField>
      </FilterItem>
      <FilterItem labelText={localizedValues.text}>
        <FilterItemField shouldCollapse={() => !state.filterText}>
          <FilterTextInput id="text" value={state.filterText} onChange={e => onFilterChange(e.currentTarget.value, "filterText")} />
        </FilterItemField>
      </FilterItem>
      <FilterItem id="statusItem" labelText={localizedValues.status}>
        <FilterItemField shouldCollapse={() => !state.selectedStatus}>
          <Dropdown
            id="status"
            placeholder={localizedValues.all}
            items={statuses}
            includeEmpty
            keySelector={item => (item as ILocalizable).messageId.value}
            displaySelector={item => localize((item as ILocalizable).messageId)}
            selected={arrayHelpers.createFrom(state.selectedStatus)}
            onSelectionChanged={items => onFilterSelect(items, "selectedStatus")}
          />
        </FilterItemField>
      </FilterItem>
      <FilterItem id="industryItem" labelText={localizedValues.industry}>
        <FilterItemField shouldCollapse={() => !state.selectedIndustry}>
          <PickerInput
            autoPick
            dialogTitle={localizedValues.industry}
            data={industries}
            keySelector={row => row.isiCv4Code}
            displaySelector={row => row.description}
            selected={selectedIndustries}
            onSelectionChanged={rows => onFilterSelect(rows, "selectedIndustry")}
            onSearch={(q, cols) => loadIndustries({ query: q, searchedColumns: cols })}
          >
            <DataColumnDef<IIndustry> key="industryIsiCv4Code" id="industryIsiCv4Code" title={localizedValues.code} valueSelector={row => row.isiCv4Code} searchable sortable>
              {row => row.isiCv4Code}
            </DataColumnDef>
            <DataColumnDef<IIndustry>
              key="industryDescription"
              id="industryDescription"
              title={localizedValues.description}
              valueSelector={row => row.description}
              searchable
              sortable
            >
              {row => row.description}
            </DataColumnDef>
          </PickerInput>
        </FilterItemField>
      </FilterItem>

      <FilterItem id="assignedToItem" labelText={localizedValues.assignedTo}>
        <FilterItemField shouldCollapse={() => !state.selectedAssignedTo}>
          <PickerInput
            autoPick
            dialogTitle={localizedValues.assignedTo}
            data={assignedToAccounts}
            keySelector={row => row.name}
            displaySelector={row => row.name}
            selected={selectedAssignedTo}
            onSelectionChanged={rows => onFilterSelect(rows, "selectedAssignedTo")}
            onSearch={(q, cols) => loadAssignedToAccounts({ query: q, searchedColumns: cols })}
          >
            <DataColumnDef<IAccount> key="assignedToName" id="assignedToName" title={localizedValues.name} valueSelector={row => row.name} searchable sortable>
              {row => row.name}
            </DataColumnDef>
          </PickerInput>
        </FilterItemField>
      </FilterItem>

      <FilterItem labelText={localizedValues.messageId}>
        <FilterItemField shouldCollapse={() => !state.filterMessageId}>
          <FilterTextInput id="messageId" value={state.filterMessageId} onChange={e => onFilterChange(e.currentTarget.value, "filterMessageId")} />
        </FilterItemField>
      </FilterItem>
      <FilterItem id="parentItem" labelText={localizedValues.parent}>
        <FilterItemField shouldCollapse={() => !state.selectedParent}>
          <Dropdown
            id="parents"
            placeholder={localizedValues.all}
            includeEmpty
            items={parents}
            keySelector={item => (item as ILocalizable).messageId.value}
            displaySelector={item => localize((item as ILocalizable).messageId)}
            selected={arrayHelpers.createFrom(state.selectedParent)}
            onSelectionChanged={items => onFilterSelect(items, "selectedParent")}
          />
        </FilterItemField>
      </FilterItem>

      <FilterItem labelText={localizedValues.showTextPreview}>
        <FilterItemField shouldCollapse={() => !state.isCheckedShowPreviewText}>
          <Checkbox id="isCheckedShowPreviewText" checked={state.isCheckedShowPreviewText} onChange={onCheckboxChange} />
        </FilterItemField>
      </FilterItem>
    </FilterSection>
  );
};

export default connect(
  (state: { plexTranslationMessagesManager: IPlexTranslationMessagesManagerState; settings: ISettingsState }) => {
    const filter = state.plexTranslationMessagesManager.filter;
    return {
      showPreviewText: filter.isCheckedShowPreviewText,
      locales: filter.locales,
      supportedLocales: filter.supportedLocales,
      currentLocaleIdentifier: state.settings.localSettings.locale,
      assignedToAccounts: filter.assignedToAccounts,
      industries: filter.industries,
      statuses: filter.statuses,
      parents: filter.parents
    };
  },
  (dispatch: Dispatch) => {
    return bindActionCreators(
      {
        applyFilter: plexTranslationMessagesActions.applyFilter,
        search: plexTranslationMessagesActions.search,
        loadIndustries: plexTranslationMessagesActions.loadIndustries,
        loadLocales: plexTranslationMessagesActions.loadLocales,
        loadSupportedLocales: plexTranslationMessagesActions.loadSupportedLocales,
        loadAssignedToAccounts: plexTranslationMessagesActions.loadAssignedToAccounts
      },
      dispatch
    );
  }
)(PlexTranslationMessagesManagerFilter);
